import { useState } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm() {
  const { t, lang } = useLanguage();
  const f = t.leadForm;

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    communities: f.communitiesOptions[1],
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error(f.error);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, locale: lang });
      setDone(true);
      toast.success(f.successTitle);
    } catch (err) {
      console.error(err);
      toast.error(f.error);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="bg-surface border border-app rounded-lg p-10 md:p-14 text-center shadow-sm"
        data-testid="lead-success"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-[#3B7A57]/10 flex items-center justify-center mb-5">
          <CheckCircle2 className="text-[#3B7A57]" size={32} />
        </div>
        <h3 className="font-serif text-3xl md:text-4xl text-app mb-3">{f.successTitle}</h3>
        <p className="text-app-secondary max-w-md mx-auto">{f.successBody}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-surface border border-app rounded-lg p-8 md:p-12 shadow-sm"
      data-testid="lead-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label={f.fields.name} required>
          <input
            name="name"
            value={form.name}
            onChange={onChange}
            required
            className="input-field"
            data-testid="lead-name"
            autoComplete="name"
          />
        </Field>
        <Field label={f.fields.email} required>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            required
            className="input-field"
            data-testid="lead-email"
            autoComplete="email"
          />
        </Field>
        <Field label={f.fields.company} required>
          <input
            name="company"
            value={form.company}
            onChange={onChange}
            required
            className="input-field"
            data-testid="lead-company"
            autoComplete="organization"
          />
        </Field>
        <Field label={f.fields.communities}>
          <select
            name="communities"
            value={form.communities}
            onChange={onChange}
            className="input-field"
            data-testid="lead-communities"
          >
            {f.communitiesOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </Field>
        <Field label={f.fields.phone}>
          <input
            name="phone"
            value={form.phone}
            onChange={onChange}
            className="input-field"
            data-testid="lead-phone"
            autoComplete="tel"
          />
        </Field>
        <div className="md:col-span-2">
          <Field label={f.fields.message}>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              rows={3}
              className="input-field resize-none"
              data-testid="lead-message"
            />
          </Field>
        </div>
      </div>

      <div className="mt-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-2 text-xs text-app-muted">
          <ShieldCheck size={14} className="mt-0.5 flex-shrink-0" />
          <span>{f.privacy}</span>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 bg-primary-app text-white font-medium px-6 py-3 rounded-md hover:bg-[#8F3E2E] transition-colors disabled:opacity-60"
          data-testid="lead-submit"
        >
          {loading ? f.submitting : f.submit}
          {!loading && <ArrowRight size={16} />}
        </button>
      </div>

      <style>{`
        .input-field {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 0.375rem;
          color: var(--text-primary);
          font-size: 0.9375rem;
          transition: border-color .15s, box-shadow .15s;
        }
        .input-field:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(179, 78, 58, 0.12);
        }
      `}</style>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-app-secondary mb-1.5">
        {label} {required && <span className="text-primary-app">*</span>}
      </span>
      {children}
    </label>
  );
}
